import React from 'react';
import { useDispatch } from 'react-redux';

import { moveAccount, replaceModal } from 'loot-core/src/client/actions';
import * as Platform from 'loot-core/src/client/platform';

import { useAccounts } from '../../hooks/useAccounts';
import { useGlobalPref } from '../../hooks/useGlobalPref';
import { useIsTablet } from '../../hooks/useIsTablet';
import { useLocalPref } from '../../hooks/useLocalPref';
import {
  SvgCalendar,
  SvgCog,
  SvgReports,
  SvgStoreFront,
  SvgTuning,
  SvgWallet,
} from '../../icons/v1';
import { DashboardIcon } from '../../icons/v2/DashboardIcon';
import { SvgPlanningIcon } from '../../icons/v2/PlanningIcon';
import { SvgCategories } from '../../icons/v2/SvgCategories';
import { styles, theme } from '../../style';
import { EditableBudgetName } from '../common/EditableBudgetName';
import { Link } from '../common/Link';
import { View } from '../common/View';

import { Accounts } from './Accounts';
import { Item } from './Item';
import { useSidebar } from './SidebarProvider';
import { ToggleButton } from './ToggleButton';

export const SIDEBAR_WIDTH = 240;

export function Sidebar() {
  const hasWindowButtons = !Platform.isBrowser && Platform.OS === 'mac';

  const dispatch = useDispatch();
  const sidebar = useSidebar();
  const accounts = useAccounts();
  const [showClosedAccounts, setShowClosedAccountsPref] = useLocalPref(
    'ui.showClosedAccounts',
  );
  const [isFloating = false, setFloatingSidebarPref] =
    useGlobalPref('floatingSidebar');
  const isTablet = useIsTablet();

  async function onReorder(
    id: string,
    dropPos: 'top' | 'bottom',
    targetId: unknown,
  ) {
    let targetIdToMove = targetId;
    if (dropPos === 'bottom') {
      const idx = accounts.findIndex(a => a.id === targetId) + 1;
      targetIdToMove = idx < accounts.length ? accounts[idx].id : null;
    }

    dispatch(moveAccount(id, targetIdToMove));
  }

  const onFloat = () => {
    setFloatingSidebarPref(!isFloating);
  };

  const onAddAccount = () => {
    dispatch(replaceModal('add-account'));
  };

  const onToggleClosedAccounts = () => {
    setShowClosedAccountsPref(!showClosedAccounts);
  };

  return (
    <View
      style={{
        width: SIDEBAR_WIDTH,
        color: theme.sidebarItemText,
        backgroundColor: theme.sidebarBackground,
        '& .float': {
          opacity: isFloating ? 1 : 0,
          transition: 'opacity .25s, width .25s',
          width: hasWindowButtons || isFloating ? null : 0,
        },
        '&:hover .float': {
          opacity: 1,
          width: hasWindowButtons ? null : 'auto',
        },
        flex: 1,
        ...styles.darkScrollbar,
      }}
    >
      <View
        style={{
          justifyContent: 'space-between',
          flex: 1,
        }}
      >
        <View style={{ flex: 1, justifyContent: 'space-between' }}>
          <View>
            <View
              style={{
                height: 30,
                marginTop: 8,
                marginRight: 8,
                flexDirection: 'row',
                alignItems: 'center',
                transition: 'padding .4s',
                ...(hasWindowButtons && {
                  justifyContent: 'flex-start',
                }),
              }}
            >
              <View style={{ flex: 1, flexDirection: 'row' }} />

              {!sidebar.alwaysFloats && (
                <ToggleButton isFloating={isFloating} onFloat={onFloat} />
              )}
            </View>

            <View
              style={{
                marginLeft: 20,
                marginBottom: 20,
              }}
            >
              <Link to="/">
                <img src="/images/gs-logo.png" width={180} />
              </Link>
            </View>

            <View style={{ overflow: 'auto' }}>
              <Item
                wrapperAdoptionId="menu-item-dashboard"
                title="Dashboard"
                Icon={DashboardIcon}
                to="/"
              />
              {isTablet ? null : (
                <Item
                  wrapperAdoptionId="menu-item-planning"
                  title="Planning"
                  Icon={SvgPlanningIcon}
                  to="/planning"
                />
              )}
              <Item
                wrapperAdoptionId="menu-item-tracking"
                title="Tracking"
                Icon={SvgWallet}
                to="/budget"
              />
              <Item
                wrapperAdoptionId="menu-item-reports"
                title="Reports"
                Icon={SvgReports}
                to="/reports"
              />

              <Item title="Schedules" Icon={SvgCalendar} to="/schedules" />
              <Item title="Categories" Icon={SvgCategories} to="/categories" />
              <Item title="Payees" Icon={SvgStoreFront} to="/payees" />
              <Item title="Rules" Icon={SvgTuning} to="/rules" />
              <Item title="Settings" Icon={SvgCog} to="/settings" />

              {/*<Tools />*/}

              <View
                style={{
                  height: 1,
                  backgroundColor: theme.sidebarItemBackgroundHover,
                  marginTop: 15,
                  flexShrink: 0,
                }}
              />

              <Accounts
                onAddAccount={onAddAccount}
                onToggleClosedAccounts={onToggleClosedAccounts}
                onReorder={onReorder}
              />
            </View>
          </View>

          <View
            style={{
              padding: '10px',
              borderTop: '1px solid ' + theme.menuBorder,
            }}
          >
            <EditableBudgetName />
          </View>
        </View>
      </View>
    </View>
  );
}
